import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Activity,
  Apple,
  Baby,
  Banknote,
  Bath,
  Bike,
  BookOpen,
  Beef,
  Beer,
  Briefcase,
  Building2,
  Cake,
  Calculator,
  Camera,
  Car,
  CarFront,
  Cat,
  ChefHat,
  Coffee,
  Cpu,
  Croissant,
  CupSoda,
  Dog,
  Drumstick,
  Dumbbell,
  Eye,
  Film,
  Fish,
  Flower2,
  Footprints,
  Fuel,
  Gamepad2,
  Gem,
  Gift,
  Glasses,
  GraduationCap,
  Hammer,
  HeartPulse,
  HelpCircle,
  Hotel,
  IceCreamCone,
  Key,
  Lamp,
  Laptop,
  Microscope,
  Music,
  Package,
  Paintbrush,
  Palette,
  PawPrint,
  Pill,
  Pizza,
  Plane,
  Printer,
  Salad,
  Sandwich,
  Scale,
  Scissors,
  Search,
  Shirt,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Smile,
  Sofa,
  Soup,
  Sparkles,
  Sprout,
  Stethoscope,
  Store,
  Syringe,
  Ticket,
  Truck,
  UtensilsCrossed,
  Warehouse,
  WashingMachine,
  Watch,
  Wheat,
  Wine,
  Wrench,
  X,
} from 'lucide-react';

/** A lucide icon component as consumed by the picker. */
type IconComponent = React.ComponentType<{ size?: number | string }>;

/** One entry of the curated catalog: the lucide name plus PT search keywords. */
interface IconOption {
  /** lucide-react export name - this is what gets persisted. */
  name: string;
  /** Portuguese keywords so a Brazilian user can search by segment. */
  keywords: string;
  Icon: IconComponent;
}

/**
 * Curated set of lucide icons relevant to local-commerce categories. Lucide
 * ships ~1500 icons; loading all of them would bloat the bundle and drown the
 * user, so the picker exposes this hand-picked list (searchable by name or by
 * Portuguese keyword) instead.
 */
export const CATEGORY_ICON_OPTIONS: IconOption[] = [
  // Alimentação
  { name: 'UtensilsCrossed', keywords: 'restaurante comida refeicao almoco jantar', Icon: UtensilsCrossed },
  { name: 'Beef', keywords: 'hamburguer lanche carne churrasco churrascaria', Icon: Beef },
  { name: 'Pizza', keywords: 'pizza pizzaria', Icon: Pizza },
  { name: 'Sandwich', keywords: 'lanchonete sanduiche lanche', Icon: Sandwich },
  { name: 'Soup', keywords: 'sopa caldo comida caseira', Icon: Soup },
  { name: 'Salad', keywords: 'salada natural saudavel vegano vegetariano', Icon: Salad },
  { name: 'Fish', keywords: 'peixe peixaria frutos do mar pescado japonesa', Icon: Fish },
  { name: 'Drumstick', keywords: 'frango assados galeteria', Icon: Drumstick },
  { name: 'IceCreamCone', keywords: 'sorvete sorveteria acai gelato picole', Icon: IceCreamCone },
  { name: 'Cake', keywords: 'bolo confeitaria doces festa aniversario', Icon: Cake },
  { name: 'Croissant', keywords: 'padaria panificadora paes cafe da manha', Icon: Croissant },
  { name: 'Coffee', keywords: 'cafe cafeteria cafeteria expresso', Icon: Coffee },
  { name: 'Beer', keywords: 'bar cervejaria chopp boteco', Icon: Beer },
  { name: 'Wine', keywords: 'adega vinho vinharia bebidas', Icon: Wine },
  { name: 'CupSoda', keywords: 'bebidas sucos refrigerante distribuidora', Icon: CupSoda },
  { name: 'ChefHat', keywords: 'gastronomia chef marmita buffet culinaria', Icon: ChefHat },
  { name: 'Apple', keywords: 'hortifruti frutas feira quitanda', Icon: Apple },
  { name: 'Wheat', keywords: 'cereais graos emporio produtos naturais', Icon: Wheat },

  // Mercado e varejo
  { name: 'ShoppingCart', keywords: 'mercado supermercado compras mercearia', Icon: ShoppingCart },
  { name: 'ShoppingBag', keywords: 'loja comercio varejo shopping', Icon: ShoppingBag },
  { name: 'Store', keywords: 'loja estabelecimento comercio local', Icon: Store },
  { name: 'Package', keywords: 'encomendas entregas distribuidora atacado', Icon: Package },
  { name: 'Warehouse', keywords: 'deposito atacado logistica armazem', Icon: Warehouse },

  // Moda e beleza
  { name: 'Shirt', keywords: 'moda roupas vestuario confeccao boutique', Icon: Shirt },
  { name: 'Footprints', keywords: 'calcados sapatos tenis sapataria', Icon: Footprints },
  { name: 'Watch', keywords: 'relojoaria relogios acessorios', Icon: Watch },
  { name: 'Glasses', keywords: 'otica oculos lentes', Icon: Glasses },
  { name: 'Gem', keywords: 'joalheria joias semijoias bijuteria', Icon: Gem },
  { name: 'Scissors', keywords: 'salao cabeleireiro barbearia corte beleza', Icon: Scissors },
  { name: 'Sparkles', keywords: 'beleza estetica manicure unhas spa', Icon: Sparkles },
  { name: 'Palette', keywords: 'maquiagem cosmeticos perfumaria arte', Icon: Palette },
  { name: 'Bath', keywords: 'spa banho massagem relaxamento', Icon: Bath },

  // Saúde e bem-estar
  { name: 'Stethoscope', keywords: 'saude clinica medico consultorio', Icon: Stethoscope },
  { name: 'Pill', keywords: 'farmacia drogaria medicamentos manipulacao', Icon: Pill },
  { name: 'HeartPulse', keywords: 'saude cardiologia emergencia hospital', Icon: HeartPulse },
  { name: 'Smile', keywords: 'odontologia dentista sorriso ortodontia', Icon: Smile },
  { name: 'Syringe', keywords: 'vacina enfermagem posto de saude', Icon: Syringe },
  { name: 'Microscope', keywords: 'laboratorio exames analises clinicas', Icon: Microscope },
  { name: 'Eye', keywords: 'oftalmologia visao otica', Icon: Eye },
  { name: 'Dumbbell', keywords: 'academia fitness musculacao crossfit', Icon: Dumbbell },
  { name: 'Activity', keywords: 'fisioterapia pilates bem estar terapia', Icon: Activity },

  // Pet
  { name: 'PawPrint', keywords: 'pet petshop animais', Icon: PawPrint },
  { name: 'Dog', keywords: 'cachorro pet adestramento banho e tosa', Icon: Dog },
  { name: 'Cat', keywords: 'gato pet felinos veterinaria', Icon: Cat },

  // Casa e serviços
  { name: 'Wrench', keywords: 'oficina manutencao reparos mecanica conserto', Icon: Wrench },
  { name: 'Hammer', keywords: 'construcao reforma marcenaria material', Icon: Hammer },
  { name: 'Paintbrush', keywords: 'pintura tintas decoracao', Icon: Paintbrush },
  { name: 'Sofa', keywords: 'moveis decoracao estofados casa', Icon: Sofa },
  { name: 'Lamp', keywords: 'iluminacao eletrica luminarias', Icon: Lamp },
  { name: 'WashingMachine', keywords: 'lavanderia eletrodomesticos', Icon: WashingMachine },
  { name: 'Key', keywords: 'chaveiro imobiliaria seguranca aluguel', Icon: Key },
  { name: 'Building2', keywords: 'empresa escritorio imobiliaria predio', Icon: Building2 },
  { name: 'Truck', keywords: 'transporte mudanca frete entrega', Icon: Truck },

  // Automotivo
  { name: 'Car', keywords: 'automotivo carro concessionaria locadora', Icon: Car },
  { name: 'CarFront', keywords: 'lava jato estetica automotiva auto center', Icon: CarFront },
  { name: 'Fuel', keywords: 'posto combustivel gasolina', Icon: Fuel },
  { name: 'Bike', keywords: 'bicicletaria bike ciclismo', Icon: Bike },

  // Tecnologia
  { name: 'Smartphone', keywords: 'celular assistencia tecnica telefonia', Icon: Smartphone },
  { name: 'Laptop', keywords: 'informatica computadores tecnologia', Icon: Laptop },
  { name: 'Cpu', keywords: 'eletronicos eletronica componentes', Icon: Cpu },
  { name: 'Printer', keywords: 'grafica impressao copiadora papelaria', Icon: Printer },
  { name: 'Camera', keywords: 'fotografia estudio filmagem', Icon: Camera },

  // Educação e cultura
  { name: 'BookOpen', keywords: 'livraria livros leitura sebo', Icon: BookOpen },
  { name: 'GraduationCap', keywords: 'educacao escola curso faculdade idiomas', Icon: GraduationCap },
  { name: 'Music', keywords: 'musica instrumentos estudio', Icon: Music },
  { name: 'Film', keywords: 'cinema produtora audiovisual', Icon: Film },
  { name: 'Gamepad2', keywords: 'games jogos lan house', Icon: Gamepad2 },

  // Turismo e lazer
  { name: 'Hotel', keywords: 'hotel pousada hospedagem', Icon: Hotel },
  { name: 'Plane', keywords: 'turismo agencia de viagens viagens', Icon: Plane },
  { name: 'Ticket', keywords: 'eventos ingressos casa de shows', Icon: Ticket },

  // Serviços profissionais
  { name: 'Briefcase', keywords: 'servicos consultoria negocios', Icon: Briefcase },
  { name: 'Scale', keywords: 'advocacia juridico advogado', Icon: Scale },
  { name: 'Calculator', keywords: 'contabilidade contador financas', Icon: Calculator },
  { name: 'Banknote', keywords: 'financeiro cambio emprestimo credito', Icon: Banknote },

  // Presentes e outros
  { name: 'Gift', keywords: 'presentes bazar festas lembrancinhas', Icon: Gift },
  { name: 'Flower2', keywords: 'floricultura flores buque', Icon: Flower2 },
  { name: 'Sprout', keywords: 'jardinagem plantas agro viveiro', Icon: Sprout },
  { name: 'Baby', keywords: 'infantil bebe enxoval criancas', Icon: Baby },
];

const OPTIONS_BY_NAME: Record<string, IconOption> = CATEGORY_ICON_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.name]: option }),
  {} as Record<string, IconOption>
);

/**
 * Resolve a persisted icon name to a renderable lucide component. Returns
 * `null` when the name is not part of the curated catalog (e.g. a legacy value
 * typed by hand before the picker existed).
 */
export function getCategoryIcon(name?: string | null): IconComponent | null {
  if (!name) return null;
  return OPTIONS_BY_NAME[name]?.Icon ?? null;
}

const normalize = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const Panel = styled.div`
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surface};
  overflow: hidden;
`;

const Selected = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.surfaceAlt};
`;

const Preview = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  border-radius: ${(p) => p.theme.radius};
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.surface};
  color: ${(p) => p.theme.primary};
`;

const SelectedInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;

  strong {
    font-size: 0.85rem;
    color: ${(p) => p.theme.textPrimary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  span {
    font-size: 0.72rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.surface};
  color: ${(p) => p.theme.textMuted};
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.35rem 0.6rem;
  border-radius: ${(p) => p.theme.radiusPill};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${(p) => p.theme.error};
    border-color: ${(p) => p.theme.error};
  }
`;

const SearchRow = styled.div`
  position: relative;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid ${(p) => p.theme.border};

  > svg {
    position: absolute;
    left: 1.35rem;
    top: 50%;
    transform: translateY(-50%);
    color: ${(p) => p.theme.textMuted};
    pointer-events: none;
  }
`;

const SearchField = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem 0.5rem 2.1rem;
  font-size: 0.85rem;
  font-family: inherit;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};

  &::placeholder {
    color: ${(p) => p.theme.textMuted};
  }
  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 0.4rem;
  padding: 0.65rem;
  max-height: 232px;
  overflow-y: auto;
`;

const IconCell = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  padding: 0.55rem 0.25rem;
  border-radius: ${(p) => p.theme.radius};
  border: 1px solid ${(p) => (p.$selected ? p.theme.primary : 'transparent')};
  background: ${(p) => (p.$selected ? `${p.theme.primary}14` : 'transparent')};
  color: ${(p) => (p.$selected ? p.theme.primary : p.theme.textSecondary)};
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s ease;

  .name {
    font-size: 0.62rem;
    font-weight: 600;
    line-height: 1.2;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: ${(p) => p.theme.surfaceAlt};
    border-color: ${(p) => p.theme.border};
  }
`;

const NoResults = styled.p`
  grid-column: 1 / -1;
  margin: 0;
  padding: 1.25rem 0.5rem;
  text-align: center;
  font-size: 0.8rem;
  color: ${(p) => p.theme.textMuted};
`;

export interface IconPickerProps {
  label?: string;
  /** Persisted lucide icon name (e.g. "Pizza"); empty string means "no icon". */
  value?: string;
  /** Emits the lucide icon name, or an empty string when the user clears it. */
  onChange: (iconName: string) => void;
  searchPlaceholder?: string;
  containerStyle?: React.CSSProperties;
}

/**
 * Icon selector backed by a curated list of `lucide-react` icons. The value is
 * the icon **name** as a string, so the persisted contract with the backend is
 * unchanged (the API keeps receiving `icon: "Pizza"`).
 */
export const IconPicker: React.FC<IconPickerProps> = ({
  label,
  value,
  onChange,
  searchPlaceholder = 'Buscar ícone (ex.: padaria, pet, farmácia)...',
  containerStyle,
}) => {
  const [term, setTerm] = useState<string>('');

  const options = useMemo(() => {
    const query = normalize(term.trim());
    if (!query) return CATEGORY_ICON_OPTIONS;
    return CATEGORY_ICON_OPTIONS.filter(
      (option) => normalize(option.name).includes(query) || normalize(option.keywords).includes(query)
    );
  }, [term]);

  const selected = value ? OPTIONS_BY_NAME[value] : undefined;
  const SelectedIcon = selected?.Icon;
  const isUnknown = !!value && !selected;

  return (
    <Group style={containerStyle}>
      {label && <Label>{label}</Label>}
      <Panel>
        <Selected>
          <Preview aria-hidden>
            {SelectedIcon ? <SelectedIcon size={18} /> : <HelpCircle size={18} />}
          </Preview>
          <SelectedInfo>
            <strong>{value || 'Nenhum ícone selecionado'}</strong>
            <span>
              {isUnknown
                ? 'Ícone fora da lista sugerida - escolha um abaixo para atualizar.'
                : value
                  ? 'Ícone atual da categoria'
                  : 'Escolha um ícone para representar a categoria'}
            </span>
          </SelectedInfo>
          {value && (
            <ClearButton type="button" onClick={() => onChange('')}>
              <X size={13} /> Limpar
            </ClearButton>
          )}
        </Selected>

        <SearchRow>
          <Search size={15} />
          <SearchField
            type="text"
            value={term}
            placeholder={searchPlaceholder}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTerm(e.target.value)}
          />
        </SearchRow>

        <Grid>
          {options.map((option) => {
            const { Icon } = option;
            return (
              <IconCell
                key={option.name}
                type="button"
                title={option.name}
                aria-pressed={value === option.name}
                $selected={value === option.name}
                onClick={() => onChange(option.name)}
              >
                <Icon size={20} />
                <span className="name">{option.name}</span>
              </IconCell>
            );
          })}
          {options.length === 0 && <NoResults>Nenhum ícone encontrado para “{term}”.</NoResults>}
        </Grid>
      </Panel>
    </Group>
  );
};

export default IconPicker;
